/**
 * A customer's order history: its CIS orders and its Tharstern work in one list (v1 completion F5). Search, system and
 * page live in the URL, so a filtered history is a link; they run on the server (`listCustomerOrderHistory`) and this
 * never filters the response (docs/list-endpoints.md). Changing a filter goes back to page 1.
 *
 * The past work is read from the copy we keep after Tharstern is switched off. Ordering it again is One's, as a line
 * on a draft quote.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CustomerOrderHistoryOptions,
  ListResponse,
} from "@shared-core/data/adapter";
import type { CustomerOrderHistoryItem } from "@shared-core/data/order-to-invoice-types";
import {
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Input } from "../../primitives/input";
import { Skeleton } from "../../primitives/skeleton";
import { ListPagination } from "../../patterns/ListPagination";

type Source = NonNullable<CustomerOrderHistoryOptions["source"]>;

const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

function sourceOf(value: string | null): Source | undefined {
  return value === "cis" || value === "tharstern" ? value : undefined;
}

/** "Job 738048", "Order 55102" or the CIS order number: how the row's number reads. */
function numberLabel(item: CustomerOrderHistoryItem): string {
  if (item.type === "order") return item.order_no ?? "";
  return item.order_no ? `Order ${item.order_no}` : `Job ${item.job_no}`;
}

function NumberCell({ item }: { item: CustomerOrderHistoryItem }) {
  const label = numberLabel(item);
  const firstJob = item.order_again_jobs[0]?.job_no;
  const target =
    item.type === "order" && item.order_no
      ? paths.orderDetail({ orderNo: item.order_no })
      : firstJob
        ? paths.jobWorkspace({ jobNo: firstJob })
        : null;
  return (
    <td className="whitespace-nowrap py-2 pr-3 font-medium tabular-nums">
      {target ? (
        <Link
          to={target}
          className="text-[color:var(--color-primary)] hover:underline"
        >
          {label}
        </Link>
      ) : (
        label
      )}
      {item.source === "tharstern" && (
        <Badge variant="outline" className="ml-2 font-normal">
          Tharstern
        </Badge>
      )}
    </td>
  );
}

function WhatCell({ item }: { item: CustomerOrderHistoryItem }) {
  const quantity = item.quantity
    ? `${item.quantity.toLocaleString("en-GB")} × `
    : "";
  if (item.what) {
    return (
      <td className="min-w-48 py-2 pr-3">
        {quantity}
        {item.what}
      </td>
    );
  }
  return (
    <td className="min-w-48 py-2 pr-3">
      <span className="block">{item.line_count} lines</span>
      {item.order_again_jobs.length > 0 && (
        <span className="block text-xs text-muted-foreground">
          {item.order_again_jobs
            .map((j) => j.description ?? `Job ${j.job_no}`)
            .join(", ")}
        </span>
      )}
    </td>
  );
}

export function CustomerOrderHistoryList({
  customerCode,
}: {
  customerCode: string;
}) {
  const adapter = useDataAdapter();
  const [params, setParams] = useSearchParams();
  const source = sourceOf(params.get("source"));
  const q = params.get("q") || undefined;
  const page = Number(params.get("page")) || 1;
  const isFiltered = !!(source || q);

  const [result, setResult] =
    useState<ListResponse<CustomerOrderHistoryItem> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setResult(null);
    adapter
      .listCustomerOrderHistory({ customerCode, source, q, page })
      .then((r) => !cancelled && setResult(r))
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Could not load the history.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerCode, source, q, page]);

  const setFilter = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          // Uncontrolled, so remount on a new q: back and forward show the URL's search.
          key={q ?? ""}
          type="search"
          placeholder="Search history"
          aria-label="Search history"
          defaultValue={q ?? ""}
          onKeyDown={(e) => {
            if (e.key === "Enter") setFilter("q", e.currentTarget.value.trim());
          }}
          className="w-full sm:w-64"
        />
        <select
          aria-label="System"
          value={source ?? ""}
          onChange={(e) => setFilter("source", e.target.value)}
          className={selectClass}
        >
          <option value="">One and Tharstern</option>
          <option value="cis">One only</option>
          <option value="tharstern">Tharstern only</option>
        </select>
      </div>

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load the history: {error}
          </AlertDescription>
        </Alert>
      )}
      {!result && !error && (
        <div className="space-y-2" aria-label="Loading history">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {result && result.items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {isFiltered
            ? "Nothing in the history matches these filters."
            : "No history yet. Orders and past Tharstern work appear here."}
        </p>
      )}
      {result && result.items.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1.5 pr-3 font-normal">Ordered</th>
                  <th className="py-1.5 pr-3 font-normal">Number</th>
                  <th className="py-1.5 pr-3 font-normal">What</th>
                  <th className="py-1.5 pr-3 text-right font-normal">Value</th>
                  <th className="py-1.5 pr-3 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((item) => (
                  <tr
                    key={`${item.source}:${item.order_no ?? item.job_no}`}
                    className="border-t border-border align-top"
                  >
                    <td className="whitespace-nowrap py-2 pr-3 tabular-nums">
                      {formatDateOrDash(item.ordered_at)}
                    </td>
                    <NumberCell item={item} />
                    <WhatCell item={item} />
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {formatQuotedPrice(item.value_pence)}
                    </td>
                    <td className="py-2 pr-3">{item.status_line}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Tharstern rows are read from the copy we keep after Tharstern is
            switched off.
          </p>
          <ListPagination
            page={result.page}
            totalPages={result.totalPages}
            onPageChange={(p) => setFilter("page", String(p))}
          />
        </>
      )}
    </div>
  );
}
